import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// 1. Global Components
import { Header } from "@/components/Header";

// Homepage loaded eagerly
import Index from "./pages/Index";

// All other pages lazy-loaded
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyArchitecture = lazy(() => import("./pages/PrivacyArchitecture"));
const Terms = lazy(() => import("./pages/Terms"));
const Refund = lazy(() => import("./pages/Refund"));
const Documentation = lazy(() => import("./pages/Documentation"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Careers = lazy(() => import("./pages/Careers"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Vulnerability = lazy(() => import("./pages/Vulnerability"));
const Contact = lazy(() => import("./pages/Contact"));
const Guidelines = lazy(() => import("./pages/Guidelines"));
const AcceptableUse = lazy(() => import("./pages/AcceptableUse"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Integrations = lazy(() => import("./pages/Integrations"));
const GmailIntegration = lazy(() => import("./pages/gmail-integration"));
const SlackIntegration = lazy(() => import("./pages/slack-integration"));
const TeamsIntegration = lazy(() => import("./pages/teams-integration"));
const OutlookIntegration = lazy(() => import("./pages/outlook-integration"));
const SmartFaq = lazy(() => import("./pages/smart-faq"));
const Workflows = lazy(() => import("./pages/Workflows"));
const LeadResponseAutomation = lazy(() => import("./pages/LeadResponseAutomation"));
const queryClient = new QueryClient();
const RR = lazy(() => import("./pages/rr"));

const Solutions = lazy(() => import("./pages/Solutions"));
const ForFounders = lazy(() => import("./pages/ForFounders"));
const ForFreelancers = lazy(() => import("./pages/ForFreelancers"));
const ForRemoteTeams = lazy(() => import("./pages/ForRemoteTeams"));
// ClearMakes Inbox search pages (SEO run 2026-09-29: gtm/products/clearmakes-inbox/seo/)
const IntegrationGmailToSlack = lazy(() => import("./pages/IntegrationGmailToSlack"));
const IntegrationOutlookToSlack = lazy(() => import("./pages/IntegrationOutlookToSlack"));
const IntegrationOutlookToTeams = lazy(() => import("./pages/IntegrationOutlookToTeams"));
const IntegrationEmailToHubspot = lazy(() => import("./pages/IntegrationEmailToHubspot"));
const GuideForwardEmailToSlack = lazy(() => import("./pages/GuideForwardEmailToSlack"));
const GuideAutoReplyCommonQuestions = lazy(() => import("./pages/GuideAutoReplyCommonQuestions"));
const AlternativesZapier = lazy(() => import("./pages/AlternativesZapier"));
const Guides = lazy(() => import("./pages/Guides"));
// New platform pages (semantic tokens only; no legacy theme needed)
const Products = lazy(() => import("./pages/Products"));
const ProductInbox = lazy(() => import("./pages/ProductInbox"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const RequestDemo = lazy(() => import("./pages/RequestDemo"));
const Company = lazy(() => import("./pages/Company"));
const Press = lazy(() => import("./pages/Press"));
const Resources = lazy(() => import("./pages/Resources"));

// Pages written for the new design system. Everything else is an older long-form
// page and gets the `.legacy` scope, which remaps its dark-theme classes.
const NEW_PAGES = new Set(["/", "/products", "/get-started", "/request-demo", "/company", "/press", "/resources"]);
const isNewPage = (pathname: string) => {
  const p = pathname.replace(/\/$/, "") || "/";
  return NEW_PAGES.has(p) || p.startsWith("/products/");
};

// Titles are not shortened in the page: search engines and link previews read the
// <title>, so every page writes a short, keyword-first title itself (see HOMEPAGE.md, SEO).

// Shown while a page's code is loading, so navigation never lands on a blank screen.
const PageSkeleton = () => (
  <div aria-busy="true" aria-label="Loading page" className="cm-container min-h-[70vh] py-16 sm:py-24">
    <div className="cm-skel h-4 w-28" />
    <div className="cm-skel mt-6 h-12 w-[min(640px,90%)]" />
    <div className="cm-skel mt-4 h-12 w-[min(460px,70%)]" />
    <div className="cm-skel mt-8 h-5 w-[min(560px,85%)]" />
    <div className="cm-skel mt-3 h-5 w-[min(420px,65%)]" />
    <div className="mt-14 grid gap-5 sm:grid-cols-3">
      {[0, 1, 2].map((i) => <div key={i} className="cm-skel h-44 rounded-2xl" />)}
    </div>
  </div>
);

// Every page below the homepage offers a way back.
const BackBar = () => {
  const { pathname, key } = useLocation();
  const navigate = useNavigate();
  const p = pathname.replace(/\/$/, "") || "/";
  if (p === "/" || p === "/get-started") return null;
  const parent = p.startsWith("/products/") ? { label: "Products", href: "/products" } : p === "/pricing" ? { label: "ClearMakes Inbox", href: "/products/inbox" } : null;
  return (
    <div className="border-b border-[hsl(216_30%_86%)] bg-[hsl(216_60%_96.5%)]">
      <div className="cm-container flex h-11 items-center gap-3 text-[14px]">
        <button
          type="button"
          onClick={() => (key !== "default" ? navigate(-1) : navigate(parent ? parent.href : "/"))}
          className="group inline-flex items-center gap-1.5 font-medium text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> Back
        </button>
        <span className="text-border">|</span>
        <Link to="/" className="text-muted-foreground transition-colors hover:text-foreground">ClearMakes</Link>
        {parent && (
          <>
            <span className="text-muted-foreground">/</span>
            <Link to={parent.href} className="text-muted-foreground transition-colors hover:text-foreground">{parent.label}</Link>
          </>
        )}
      </div>
    </div>
  );
};

const PageShell = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  return (
    <>
      <BackBar />
      <div className={isNewPage(pathname) ? undefined : "legacy"}>{children}</div>
    </>
  );
};
// A product that lives on its own site: leave the app for it.
const ExternalRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
};

// Focused, single-task screens carry their own minimal top bar.
const SiteHeader = () => {
  const { pathname } = useLocation();
  return pathname.replace(/\/$/, "") === "/get-started" ? null : <Header />;
};
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const CanonicalUpdater = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const canonical = `https://clearmakes.com${pathname === "/" ? "/" : pathname.replace(/\/$/, "")}`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
  }, [pathname]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <CanonicalUpdater />
        <SiteHeader />
        
        <PageShell>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            {/* Core Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/inbox" element={<ProductInbox />} />
            {/* Agents and ClearMeet live on their own sites; the server should 301 these too (gtm/site/server-seo.md) */}
            <Route path="/products/agents" element={<ExternalRedirect to="https://agents.clearmakes.com/" />} />
            <Route path="/products/meet" element={<ExternalRedirect to="https://meet.clearmakes.com/" />} />
            <Route path="/products/clearmeet" element={<ExternalRedirect to="https://meet.clearmakes.com/" />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/request-demo" element={<RequestDemo />} />
            <Route path="/company" element={<Company />} />
            <Route path="/press" element={<Press />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/homepage" element={<Navigate to="/" replace />} />
            
            <Route path="/homepage.html" element={<Navigate to="/" replace />} />
            {/* merged into /company on 2026-09-28; the server should 301 /about too */}
            <Route path="/about" element={<Navigate to="/company" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Navigate to="/products/inbox" replace />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/vulnerability" element={<Vulnerability />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/guidelines" element={<Guidelines />} />
            <Route path="/acceptable-use" element={<AcceptableUse />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/security-architecture" element={<PrivacyArchitecture />} />

            
            <Route path="/rr" element={<RR />} />
            
            {/* AI & Summary Features */}
            <Route path="/ai-communication-assistant" element={<Navigate to="/products/inbox" replace />} />
            <Route path="/ai-message-assistant" element={<Navigate to="/products/inbox" replace />} />

            {/* Integration Routes */}
            <Route path="/gmail-integration" element={<GmailIntegration />} />
            <Route path="/slack-integration" element={<SlackIntegration />} />
            <Route path="/teams-integration" element={<TeamsIntegration />} />
            <Route path="/outlook-integration" element={<OutlookIntegration />} />
            <Route path="/smart-faq" element={<SmartFaq />} />
            <Route path="/workflows" element={<Workflows />} />
            <Route path="/lead-response-automation" element={<LeadResponseAutomation />} />
            <Route path="/best-ai-email-tools" element={<Navigate to="/products/inbox" replace />} />
            <Route path="/superhuman-alternative-for-teams" element={<Navigate to="/products/inbox" replace />} />
            <Route path="/zapier-alternative-messaging" element={<Navigate to="/alternatives/zapier" replace />} />
            <Route path="/clearmakes-vs-competitors" element={<Navigate to="/alternatives/zapier" replace />} />
            <Route path="/why-clearmakes" element={<Navigate to="/products/inbox" replace />} />
            <Route path="/clearmakes-vs-calendly" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-intercom" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-help-scout" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-monday" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-notion-ai" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-clickup" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-zendesk" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-gmail-gemini" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-outlook-copilot" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-spark-mail" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-rambox" element={<Navigate to="/integrations" replace />} />
<Route path="/clearmakes-vs-slack-threads" element={<Navigate to="/integrations" replace />} />


<Route path="/clearmakes-vs-front" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-superhuman" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-shortwave" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-sanebox" element={<Navigate to="/products/inbox" replace />} />
<Route path="/clearmakes-vs-beeper" element={<Navigate to="/integrations" replace />} />
<Route path="/solutions" element={<Solutions />} />
<Route path="/for-founders" element={<ForFounders />} />
<Route path="/for-freelancers" element={<ForFreelancers />} />
<Route path="/for-remote-teams" element={<ForRemoteTeams />} />





            {/* ClearMakes Inbox search pages (SEO run 2026-09-29) */}
            <Route path="/integrations/gmail-to-slack" element={<IntegrationGmailToSlack />} />
            <Route path="/integrations/outlook-to-slack" element={<IntegrationOutlookToSlack />} />
            <Route path="/integrations/outlook-to-teams" element={<IntegrationOutlookToTeams />} />
            <Route path="/integrations/email-to-hubspot" element={<IntegrationEmailToHubspot />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guides/forward-email-to-slack-channel" element={<GuideForwardEmailToSlack />} />
            <Route path="/guides/auto-reply-to-common-customer-questions" element={<GuideAutoReplyCommonQuestions />} />
            <Route path="/alternatives/zapier" element={<AlternativesZapier />} />

            {/* Catch-all 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </PageShell>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;